import { useState } from 'react';
import { nomeAnexo } from '../lib/nomeArquivo';
import { ActivityIndicator, Alert, Linking, Pressable, ScrollView, Text, View } from 'react-native';
import { LISTA_ITENS } from '../lib/enviarPlanner';
import { enviarVariosAoPlanner, perguntarEnvio, prefillDe } from '../lib/envioPlanner';   // texto 10 (E1)
import { useDoc } from '../store/store';
import { AcoesItem } from '../components/Acoes';
import { Ordenavel } from '../components/Arrastar';
import { Arquivados, useSelecao } from '../components/Selecao';
import { useOrganizar } from '../lib/organizar';
import { paraLixeira } from '../lib/lixeira';
import { useBotaoNovo } from '../lib/botaoNovo';
import { useNovoComNota } from '../components/NotaRapida';
import type { Warranty } from '../store/types';
import { saveWarranty, deleteWarranty, warrantyEnd, daysLeft, alertasDaGarantia } from '../store/warranty';
import { semBloqueio } from '../auth/AuthContext';
import { validarLink } from '../modules/planner/links';
import { useTaskSheet } from '../components/TaskSheet';
import { PickerModal } from '../components/Pickers';
import { AlertasEditor } from '../components/AlertasEditor';
import { opcoesDias } from '../lib/alertasData';
import { ActionSheet, Btn, Chip, Field, Screen, Sheet, type Action } from '../components/ui';
import { CampoRico } from '../components/CampoRico';
import { PaginaRolavel } from '../components/PaginaEditor';
import { AnexosEditor } from '../components/Anexos';
import { ReceivedBoxes } from '../components/BoxCard';
import { abrirAnexo, abreAqui, lerBase64, temLocal } from '../lib/anexos';
import { escolherEGuardar } from '../lib/nuvem';
import { iaPronta } from '../lib/ia';
import { lerFicha } from '../lib/iaUsos';
import { useFonteIA } from '../components/FonteIA';
import { whatsapp } from '../lib/mapa';
import type { Campo } from '../modulos/tipos';
import { dinheiro, lerValor, moedaPadrao } from '../lib/moeda';


const CATEGORIAS: [string, string][] = [[N_('Eletrodoméstico'), '🧺'], [N_('Eletrônico'), '📱'], [N_('Informática'), '💻'], [N_('Móvel'), '🛋️'],
  [N_('Ferramenta'), '🔧'], [N_('Veículo e acessórios'), '🚗'], [N_('Outro'), '📦']];
const iconeCat = (c?: string) => CATEGORIAS.find(x => x[0] === c)?.[1] ?? '🧾';
// o que a IA procura na nota fiscal ou no certificado de garantia
const CAMPOS_NF: Campo[] = [
  { k: 'name', r: 'Produto (nome curto, com marca e modelo)', t: 'texto' }, { k: 'store', r: 'Loja (emitente da nota)', t: 'texto' },
  { k: 'purchase', r: 'Data da compra (emissão da nota)', t: 'data' }, { k: 'valor', r: 'Valor pago pelo produto', t: 'dinheiro' },
  { k: 'categoria', r: 'Tipo do produto', t: 'opcao', op: CATEGORIAS.map(x => x[0]) },
  { k: 'months', r: 'Meses de garantia do fabricante (se aparecer)', t: 'numero' }, { k: 'notes', r: 'Número de série ou modelo', t: 'texto' },
  { k: 'supportName', r: 'Assistência técnica (se aparecer)', t: 'texto' }, { k: 'supportPhone', r: 'Telefone da assistência (se aparecer)', t: 'telefone' },
];
import { C, alpha, useCores } from '../components/theme';
import { brYmd, norm, parseYmd, today, uid, ymd } from '../lib/date';
import { N_, t } from '../lib/i18n';
import { useCaixas } from '../lib/caixas';

const abrir = (url: string) => semBloqueio(() => Linking.openURL(url)).catch(() => Alert.alert(t('Não foi possível abrir')));
const vazio = (): Warranty => ({ id: uid(), name: '', store: '', purchase: today(), legalDays: 90, months: 12, endOverride: null,
  invoice: '', supportName: '', supportPhone: '', supportUrl: '', notes: '' });

export default function Garantias({ item, onBack }: { item?: string; onBack: () => void }) {
  const { items } = useDoc('garantias');
  const { corDe } = useCores();
  const cor = corDe('garantias');
  const cx = useCaixas();   // "Tamanho das caixas" (Grande = como antes)
  const sheet = useTaskSheet();
  const [ativas, setAtivas] = useState(true);
  const [filtro, setFiltro] = useState('');
  const [f, setF] = useState<Warranty | null>(null);
  const [picker, setPicker] = useState<'p' | 'o' | null>(null);
  const [menu, setMenu] = useState<{ t: string; a: Action[] } | null>(null);
  const [valorTxt, setValorTxt] = useState('');
  const abrirFicha = (w: Warranty) => { setValorTxt(w.valor ? String(w.valor).replace('.', ',') : ''); setF(w); };
  const novo = f && !items.some(x => x.id === f.id);
  const org = useOrganizar('garantias');
  const novoTela = useNovoComNota('garantias', { rotulo: t('Nova garantia'), fn: () => abrirFicha(vazio()) });   // texto 29: + com Nota rápida
  const caixas = useDoc('anotacoes').boxes;
  /** Excluir: uma cópia vai para a lixeira (30 dias). */
  const excluir = (w: Warranty) => { paraLixeira('garantias', 'lista', w.name, w, { doc: 'garantias', campo: 'items' }); deleteWarranty(w.id); };
  const confirmarExcluir = (w: Warranty) => Alert.alert(t('Excluir "{nome}"?', { nome: w.name }), t('Fica 30 dias na lixeira.'), [
    { text: t('Cancelar'), style: 'cancel' }, { text: t('Excluir'), style: 'destructive', onPress: () => excluir(w) }]);

  const [lendo, setLendo] = useState(false);
  const fonteIA = useFonteIA();
  /** Preenche a garantia lendo a nota fiscal (foto ou PDF) com a IA do usuário. */
  async function preencherComIA() {
    if (!f) return;
    if (!(await iaPronta())) return Alert.alert(t('IA não ligada'), t('Ligue a sua IA em Ajustes › Inteligência artificial.'));
    const ja = (f.arquivos ?? []).find(a => temLocal(a) && (a.mime?.startsWith('image/') || a.mime === 'application/pdf'));
    try {
      const r = await fonteIA.escolher(false, ja);
      if (!r) return;
      const arq = r.arq;
      if (arq !== ja) setF(x => (x ? { ...x, arquivos: [...(x.arquivos ?? []), arq] } : x));
      setLendo(true);
      const { c } = await lerFicha(t('Garantia'), CAMPOS_NF, { mime: arq.mime || 'image/jpeg', base64: await lerBase64(arq) });
      setF(x => {
        if (!x) return x;
        const n: any = { ...x };
        for (const k of ['name', 'store', 'categoria', 'notes', 'supportName', 'supportPhone']) if (c[k] && !n[k]) n[k] = c[k];
        if (c.purchase) n.purchase = c.purchase;
        if (typeof c.months === 'number' && c.months >= 0 && c.months <= 120) n.months = Math.round(c.months);
        return n;
      });
      if (typeof c.valor === 'number' && !valorTxt.trim()) setValorTxt(String(c.valor).replace('.', ','));
      Alert.alert(t('Ficha preenchida'), t('Confira os campos antes de salvar.'));
    } catch (e: any) { Alert.alert(t('Não foi possível'), String(e?.message ?? e)); }
    finally { setLendo(false); }
  }

  function salvar() {
    if (!f) return;
    if (!f.name.trim()) return Alert.alert(t('Informe o nome do produto.'));
    if (f.invoice.trim() && !validarLink(f.invoice)) return Alert.alert(t('Link inválido'), t('Use um link https do Google Drive, Google Docs, OneDrive ou SharePoint.'));
    if (f.supportUrl.trim() && !/^https:\/\/\S+$/i.test(f.supportUrl.trim())) return Alert.alert(t('O site deve começar com https://'));
    const v = valorTxt.trim() ? lerValor(valorTxt) : NaN;
    const w: Warranty = { ...f, valor: isFinite(v) ? v : null, name: f.name.trim(), invoice: f.invoice.trim(), supportUrl: f.supportUrl.trim(), supportPhone: f.supportPhone.replace(/[^\d+]/g, '') };
    saveWarranty(w);
    setF(null);
    // texto 10 (E1): garantia nova (tem a data do fim) pergunta se vai para o My Planner
    if (novo) perguntarEnvio({ aba: 'garantias', id: w.id, titulo: w.name, emoji: iconeCat(w.categoria), data: warrantyEnd(w), lista: LISTA_ITENS });
  }

  function opcoes(w: Warranty) {
    const a: Action[] = [];
    if (w.invoice) a.push({ label: '📄 ' + t('Abrir nota fiscal'), onPress: () => abrir(w.invoice) });
    for (const arq of w.arquivos ?? []) if (abreAqui(arq)) a.push({ label: '📎 ' + t('Abrir {nome}', { nome: nomeAnexo(arq) }), onPress: () => semBloqueio(() => abrirAnexo(arq)).catch(() => {}) });
    if (w.supportPhone) a.push({ label: '📞 ' + (w.supportName ? t('Ligar para {nome}', { nome: w.supportName }) : t('Ligar para a assistência')), onPress: () => abrir(`tel:${w.supportPhone}`) });
    if (w.supportPhone) a.push({ label: '💬 ' + t('WhatsApp da assistência'), onPress: () => whatsapp(w.supportPhone, t('Olá! Preciso de assistência técnica para: {item}', { item: w.name })) });
    if (w.supportUrl) a.push({ label: '🌐 ' + t('Site da assistência'), onPress: () => abrir(w.supportUrl) });
    // texto 21 (B2): item por item, a data do fim da garantia vai para o My Planner
    a.push({ label: '📅 ' + t('Enviar para o My Planner'), onPress: () => { const pre = prefillDe({ aba: 'garantias', id: w.id, titulo: w.name }); if (pre) sheet.open({ date: pre.start, prefill: pre }); } });
    a.push({ label: '📅 ' + t('Virar tarefa (levar à assistência)'), onPress: () => sheet.open({ prefill: {
      title: t('Assistência: {nome}', { nome: w.name }), area: 'garantias', energy: 3, link: { aba: 'garantias', id: w.id, label: w.name },
      notes: t('Garantia até {data}.', { data: brYmd(warrantyEnd(w)) }) + (w.supportName ? ' ' + t('Assistência: {nome}', { nome: w.supportName }) : '') + (w.supportPhone ? ` ${w.supportPhone}` : '') } }) });
    a.push({ label: t('Editar'), onPress: () => abrirFicha(w) });
    a.push({ label: '📦 ' + t('Arquivar'), onPress: () => org.arquivar(w.id) });
    a.push({ label: t('Excluir'), destructive: true, onPress: () => confirmarExcluir(w) });
    setMenu({ t: w.name, a });
  }

  const sep = org.separar(items, w => w.id);
  const lista = sep.ativos.map(w => ({ w, dl: daysLeft(w), fix: org.fixado(w.id) }))
    .filter(({ w }) => !filtro.trim() || norm(`${w.name} ${w.store} ${w.notes}`).includes(norm(filtro.trim())))
    .filter(({ dl }) => (ativas ? dl >= 0 : dl < 0))
    .sort((a, b) => (+b.fix - +a.fix) || (ativas ? a.dl - b.dl : b.dl - a.dl));   // fixadas primeiro
  const sel = useSelecao({ itens: lista, chave: x => x.w.id, cor, onArquivar: ids => org.arquivar(ids), genero: 'f',
    // texto 21 (B3): selecionar várias e enviar de uma vez (a data do fim da garantia)
    extras: [{ label: '📅 ' + t('Enviar para o My Planner'), onPress: ids => enviarVariosAoPlanner(ids.map(x => ({ aba: 'garantias' as const, id: x, titulo: items.find(w => w.id === x)?.name ?? '' }))) }],
    onExcluir: ids => ids.forEach(id => { const w = items.find(x => x.id === id); if (w) excluir(w); }) });

  return (
    <Screen title={t('Garantias')} onBack={onBack}>
      <Field value={filtro} onChangeText={setFiltro} placeholder={t('Buscar produto, loja ou observação')} />
      <View style={{ flexDirection: 'row', gap: 8 }}>
        <Chip cor={cor} on={ativas} label={t('Ativas')} onPress={() => setAtivas(true)} />
        <Chip cor={cor} on={!ativas} label={t('Vencidas')} onPress={() => setAtivas(false)} />
      </View>
      {/* texto 21 (B1): sem o cartão da lista inteira; o envio é item por item */}
      {!lista.length && <Text style={{ color: C.sub }}>{ativas ? t('Nenhuma garantia ativa. Cadastre com o +.') : t('Nenhuma garantia vencida.')}</Text>}
      {sel.barra}
      <Ordenavel itens={lista} chave={x => x.w.id} gap={cx.gap(10)} desligado onOrdem={() => {}} selecao={sel.prop} render={({ w, dl }) => {
        const c = dl < 0 ? C.mute : dl <= 3 ? C.danger : dl <= 15 ? C.warn : C.ok;
        return (
          <AcoesItem lembrar={{ aba: 'garantias', id: w.id, titulo: w.name }} raio={cx.raio(16)} nome={w.name} onExcluir={() => excluir(w)} onArquivar={() => org.arquivar(w.id)}
            fixar={{ on: org.fixado(w.id), alternar: () => org.alternarFixo(w.id) }}
            onDuplicar={() => saveWarranty({ ...w, id: uid(), name: t('{titulo} (cópia)', { titulo: w.name }), arquivos: [] })}
            pacote={() => ({ titulo: w.name, emoji: iconeCat(w.categoria), data: warrantyEnd(w), origem: 'garantias',
              texto: [w.store, t('Garantia até {data}.', { data: brYmd(warrantyEnd(w)) }), w.supportName, w.supportPhone].filter(Boolean).join('\n') })}>
          <Pressable onPress={() => opcoes(w)} style={{ backgroundColor: C.card, borderRadius: cx.raio(16), ...cx.pads(14), gap: cx.gap(4), borderWidth: 1, borderColor: w.id === item ? cor : C.line }}>
            <Text style={{ color: C.text, fontWeight: '700', fontSize: 16 }}>{iconeCat(w.categoria)} {w.name}</Text>
            <Text style={{ color: C.sub, fontSize: 12 }}>{w.categoria ? `${t(w.categoria)} · ` : ''}{w.store ? `${w.store} · ` : ''}{t('comprado em {data}', { data: brYmd(w.purchase) })}{w.valor ? ` · ${dinheiro(w.valor, moedaPadrao())}` : ''}{w.arquivos?.length ? ` · 📎${w.arquivos.length}` : ''}</Text>
            <Text style={{ color: c, fontWeight: '700' }}>
              {dl < 0 ? t('Venceu há {n} dia(s)', { n: -dl }) : dl === 0 ? t('Vence hoje') : t('Vence em {n} dia(s)', { n: dl })} · {t('até {data}', { data: brYmd(warrantyEnd(w)) })}
            </Text>
          </Pressable>
          </AcoesItem>
        );
      }} />
      <Arquivados itens={sep.arquivados} chave={w => w.id} cor={cor} render={w => <Text style={{ color: C.text }}>{iconeCat(w.categoria)} {w.name}</Text>}
        onRestaurar={w => org.restaurar(w.id)} onExcluir={confirmarExcluir} />
      <View style={{ height: 90 }} />

      <Sheet visible={!!f} onClose={() => setF(null)} title={novo ? t('Nova garantia') : t('Editar garantia')}
        right={<Pressable onPress={salvar}><Text style={{ color: cor, fontWeight: '800' }}>{t('Salvar')}</Text></Pressable>}>
        {f && (
          <PaginaRolavel keyboardShouldPersistTaps="handled" contentContainerStyle={{ padding: 16, gap: 12, paddingBottom: 60 }}>
            <Pressable onPress={preencherComIA} disabled={lendo} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, borderRadius: 12, backgroundColor: alpha(cor, 0.15) }}>
              {lendo ? <ActivityIndicator color={cor} /> : <Text style={{ fontSize: 18 }}>🤖</Text>}
              <Text style={{ color: C.text, fontWeight: '700', flex: 1 }}>{lendo ? t('Lendo o documento...') : t('Preencher pela nota fiscal com a minha IA')}</Text>
            </Pressable>
            <Field value={f.name} onChangeText={v => setF({ ...f, name: v })} placeholder={t('Produto (ex.: Geladeira)')} />
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
              {CATEGORIAS.map(([c, ic]) => <Chip key={c} small cor={cor} on={f.categoria === c} label={`${ic} ${t(c)}`} onPress={() => setF({ ...f, categoria: f.categoria === c ? undefined : c })} />)}
            </View>
            <Field value={f.store} onChangeText={v => setF({ ...f, store: v })} placeholder={t('Loja (opcional)')} />
            <Field value={valorTxt} onChangeText={setValorTxt} keyboardType="decimal-pad" placeholder={t('Valor pago (opcional)')} />
            <Text style={{ color: C.sub }}>{t('Data da compra')}</Text>
            <View style={{ flexDirection: 'row' }}><Chip cor={cor} on={false} label={`📅 ${brYmd(f.purchase)}`} onPress={() => setPicker('p')} /></View>
            <Text style={{ color: C.sub }}>{t('Garantia legal')}</Text>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              {[30, 90].map(d => <Chip key={d} cor={cor} on={f.legalDays === d} label={t('{n} dias', { n: d })} onPress={() => setF({ ...f, legalDays: d })} />)}
            </View>
            <Text style={{ color: C.sub }}>{t('Fabricante ou estendida: {n} meses', { n: f.months })}</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
              {[0, 6, 12, 24, 36].map(m => <Chip key={m} cor={cor} on={f.months === m} label={String(m)} onPress={() => setF({ ...f, months: m })} />)}
              <Chip cor={cor} on={false} label="−1" onPress={() => setF({ ...f, months: Math.max(0, f.months - 1) })} />
              <Chip cor={cor} on={false} label="+1" onPress={() => setF({ ...f, months: f.months + 1 })} />
            </View>
            <View style={{ backgroundColor: alpha(cor, 0.12), padding: 12, borderRadius: 12, gap: 6 }}>
              <Text style={{ color: C.text }}>{t('Fim da garantia:')} <Text style={{ fontWeight: '800' }}>{brYmd(warrantyEnd(f))}</Text>{f.endOverride ? ` (${t('ajustado')})` : ''}</Text>
              <View style={{ flexDirection: 'row', gap: 16 }}>
                <Pressable onPress={() => setPicker('o')}><Text style={{ color: cor }}>{t('Ajustar a data final')}</Text></Pressable>
                {f.endOverride && <Pressable onPress={() => setF({ ...f, endOverride: null })}><Text style={{ color: C.sub }}>{t('Voltar ao cálculo')}</Text></Pressable>}
              </View>
              <Text style={{ color: C.sub, fontSize: 11 }}>{t('Soma a garantia legal e a do fabricante. Confira no termo de garantia.')}</Text>
            </View>
            {/* texto 10 (A4): o mesmo editor de alertas da Nova tarefa (dias antes, hora e "Como avisar") */}
            <Text style={{ color: C.sub }}>🔔 {t('Alertas do fim da garantia')}</Text>
            <AlertasEditor itens={f.alertas ?? alertasDaGarantia} temHorario={false} cor={cor} aba="garantias" dias={opcoesDias([15, 60])}
              onChange={l => setF({ ...f, alertas: l })} />
            <Text style={{ color: C.sub }}>{t('Nota fiscal, certificado de garantia e fotos')}</Text>
            <AnexosEditor lista={f.arquivos ?? []} cor={cor} onChange={arquivos => setF(x => (x ? { ...x, arquivos } : x))} />
            <Field value={f.invoice} onChangeText={v => setF({ ...f, invoice: v })} autoCapitalize="none" autoCorrect={false} placeholder={t('Link da nota fiscal (Drive ou OneDrive)')} />
            <Field value={f.supportName} onChangeText={v => setF({ ...f, supportName: v })} placeholder={t('Assistência técnica (nome)')} />
            <Field value={f.supportPhone} onChangeText={v => setF({ ...f, supportPhone: v })} keyboardType="phone-pad" placeholder={t('Telefone da assistência')} />
            <Field value={f.supportUrl} onChangeText={v => setF({ ...f, supportUrl: v })} autoCapitalize="none" autoCorrect={false} placeholder={t('Site da assistência (https://...)')} />
            {/* 11f H: observações no editor das páginas (notes continua com o texto simples) */}
            <Text style={{ color: C.sub }}>{t('Observações')}</Text>
            <CampoRico valor={f.notes} rico={f.notesRico} cor={cor} aba="garantias" dica={t('Observações (nº de série, modelo...)')}
              onChange={(tx, rico) => setF(x => (x ? { ...x, notes: tx, notesRico: rico } : x))} />
            <Btn cor={cor} label={t('Salvar')} onPress={salvar} />
          </PaginaRolavel>
        )}
        {picker && f && <PickerModal mode="date" cor={cor} value={parseYmd(picker === 'p' ? f.purchase : warrantyEnd(f))} onClose={() => setPicker(null)}
          onPick={d => setF(x => (x ? (picker === 'p' ? { ...x, purchase: ymd(d) } : { ...x, endOverride: ymd(d) }) : x))} />}
        {fonteIA.tela}
      </Sheet>
      <ActionSheet title={menu?.t ?? ''} actions={menu?.a ?? null} onClose={() => setMenu(null)} />
      <ReceivedBoxes boxes={caixas} abaId="garantias" />
      {novoTela}
    </Screen>
  );
}
